'use client';

import { Swap } from '@velocity-exchange/icons';
import {
	BASE_PRECISION_EXP,
	BigNum,
	MarketType,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	UIMarket,
	calculatePnlPctFromPosition,
} from '@velocity-exchange/common';
import { getMarginUsedForPosition } from 'src/utils/position';
import React, { useEffect, useMemo, useState } from 'react';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { DefinitionTooltipID } from 'src/components/Tooltip/DefinitionTooltip/definitionTooltipConfig';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import { ZERO_BIGNUM, ZERO_PRICE_BIGNUM } from 'src/constants/math';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useShowAccountValues from 'src/hooks/useShowAccountValues';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import useMarkPrice from 'src/hooks/useMarkPrice';
import MarketIcon from '../../Utils/MarketIcon';
import useClosePositionWithMarketOrder from 'src/hooks/positions/useClosePositionWithMarketOrder';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import SideDrawer from 'src/components/Drawers/BaseDrawers/SideDrawer/SideDrawer';
import MarketCellV2 from 'src/components/Tables/cells/MarketCellV2';
import { MODAL_PADDING } from 'src/components/primitives/BaseModal/consts';
import { cj } from 'src/utils/classNames';
import { signDependentValue } from 'src/utils/signDependentValue';
import Divider from 'src/components/primitives/Divider/Divider';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import Button from 'src/components/primitives/Button/Button';

const DetailRow = ({
	label,
	value,
	marketSymbol,
	tooltipDefinitionId,
}: {
	label: React.ReactNode;
	value: React.ReactNode;
	marketSymbol?: string;
	tooltipDefinitionId?: DefinitionTooltipID;
}) => {
	const isMobile = useIsMobileScreenSize();

	const labelElement = (
		<span className="typo-body-xs text-text-secondary">{label}</span>
	);
	return (
		<div className="flex w-full justify-between">
			<DefinitionTooltip
				definitionId={tooltipDefinitionId}
				side={isMobile ? 'bottom' : 'left'}
			>
				{labelElement}
			</DefinitionTooltip>

			<div className="flex items-center gap-1">
				{marketSymbol && (
					<MarketIcon marketSymbol={marketSymbol} sizeClass="w-4 h-4" />
				)}
				<span className="typo-body-xs text-text-default">{value}</span>
			</div>
		</div>
	);
};

export const PositionDrawer = () => {
	const setDrawers = useDrawersStore((s) => s.set);
	const marketIndex = useDrawersStore((s) => s.position.marketIndex);

	const showAccountValues = useShowAccountValues();
	const perpMarketsLookup = usePerpMarketsLookup();

	const currentAccountOpenPositions = useCurrentAccountPositions();

	const [
		maintenancePositionWeightedValue,
		setMaintenancePositionWeightedValue,
	] = useState(ZERO_BIGNUM);
	const [initialPositionWeightedValue, setInitialPositionWeightedValue] =
		useState(ZERO_BIGNUM);
	const [maintenanceMarginRatio, setMaintenanceMarginRatio] = useState(0);
	const [initialMarginRatio, setInitialMarginRatio] = useState(0);

	const position = currentAccountOpenPositions
		?.filter((pos) => !pos.baseSize.eq(ZERO))
		.find((pos) => pos.marketIndex === marketIndex);

	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();
	const user =
		driftClient && driftClientIsReady && driftClient.hasUser()
			? driftClient.getUser()
			: undefined;

	const perpPosition = user?.getPerpPosition(marketIndex);
	const isIsolated = perpPosition
		? user?.isPerpPositionIsolated(perpPosition)
		: false;

	const marketConfig = perpMarketsLookup[marketIndex];

	const [pnlReference, setPnlReference] = useSyncLocalStorage<
		'mark' | 'oracle'
	>('pnlReference', 'mark');

	// const isLong = position?.direction === 'long';

	const marketId = useMemo(() => {
		return MarketId.createPerpMarket(position?.marketIndex ?? 0);
	}, [position]);

	const markPrice = useMarkPrice(marketId);

	const getOraclePrice = useGetOraclePriceForMarket();
	const oraclePrice = getOraclePrice(marketId);

	const uiMarket = new UIMarket(position?.marketIndex || 0, MarketType.PERP);
	const direction = position?.baseSize?.isNeg() ? 'short' : 'long';

	const notional = showAccountValues
		? BigNum.from(position?.notional || ZERO, QUOTE_PRECISION_EXP).toNotional()
		: '∗∗∗∗∗∗';

	const pnlToUse = BigNum.from(
		pnlReference === 'mark' ? position?.pnlVsMark : position?.pnlVsOracle,
		QUOTE_PRECISION_EXP
	);

	const quoteBreakEvenAmount = BigNum.from(
		position?.quoteBreakEvenAmount || ZERO,
		QUOTE_PRECISION_EXP
	);

	const baseSizeBigNum = BigNum.from(
		position?.baseSize?.abs() || ZERO,
		BASE_PRECISION_EXP
	);

	const breakEvenPrice = BigNum.fromPrint(
		baseSizeBigNum.gtZero()
			? `${quoteBreakEvenAmount.abs().toNum() / baseSizeBigNum.toNum()}`
			: '0',
		PRICE_PRECISION_EXP
	);

	const liqPrice = BigNum.from(position?.liqPrice || ZERO, PRICE_PRECISION_EXP);

	const entryPrice = BigNum.from(
		position?.entryPrice || ZERO,
		PRICE_PRECISION_EXP
	);

	const fundingPnl = BigNum.from(
		position?.feesAndFundingPnl ?? 0,
		QUOTE_PRECISION_EXP
	);

	const fundinPnlNotional = fundingPnl.shift(-4);

	const fundingPnlDisplay = showAccountValues
		? fundinPnlNotional.toNotional()
		: '∗∗∗∗∗∗';

	const predictedFundings = useDriftStore((s) => s.predictedFundings);

	const estHourlyFunding = useMemo(() => {
		if (!position) return null;
		const funding = predictedFundings.find(
			(f) => f.marketIndex === marketIndex
		);
		if (!funding) return null;
		const notionalNum = BigNum.from(
			position.notional || ZERO,
			QUOTE_PRECISION_EXP
		).toNum();
		// funding.fundingRate is per-hour. Positive rate = longs pay shorts.
		const sign = direction === 'long' ? -1 : 1;
		return sign * funding.fundingRate * notionalNum;
	}, [predictedFundings, position, marketIndex, direction]);

	const estHourlyFundingDisplay = useMemo(() => {
		if (estHourlyFunding === null) return null;
		if (!showAccountValues) return '∗∗∗∗∗∗';
		return BigNum.fromPrint(
			(estHourlyFunding / 100).toFixed(6),
			QUOTE_PRECISION_EXP
		).toNotional();
	}, [estHourlyFunding, showAccountValues]);

	const netPnl = pnlToUse.add(fundingPnl);

	const positionDollarPnl = showAccountValues
		? `${pnlToUse.toNotional()}`
		: '∗∗∗∗∗∗';
	const netDollarPnl = showAccountValues ? `${netPnl.toNotional()}` : '∗∗∗∗∗∗';

	const percentageNum = position
		? calculatePnlPctFromPosition(
				netPnl.val,
				position,
				user
					? getMarginUsedForPosition(user, position.marketIndex, false)
					: undefined
			)
		: 0;

	const percentagePnl = `${percentageNum.toFixed(2)}%`;

	const handleClose = () => {
		setDrawers((state) => {
			state.position.show = false;
			state.position.marketIndex = -1;
		});
	};

	const {
		closePositionWithMarketOrder,
		isPending: isClosingPosition,
		showClosePositionConfirmation,
	} = useClosePositionWithMarketOrder({
		marketIndex,
		baseSize: BigNum.from(position?.baseSize.abs(), BASE_PRECISION_EXP),
		onClosePositionSuccess: handleClose,
	});

	const handleShowClosePositionModal = () => {
		closePositionWithMarketOrder();

		// Close the drawer if we're showing the confirmation modal.
		if (showClosePositionConfirmation) {
			handleClose();
		}
	};

	const togglePnlReference = () => {
		setPnlReference(pnlReference === 'mark' ? 'oracle' : 'mark');
	};

	const estNetPnl = (
		<span
			className={cj(
				'typo-body-xs',
				signDependentValue(netPnl.toNum(), {
					positive: 'text-text-positive-green-button',
					negative: 'text-text-negative-red-button',
					zero: 'text-text-default',
				})
			)}
		>
			{netDollarPnl} ({percentagePnl})
		</span>
	);

	const getAndSetPositionHealth = () => {
		if (!user) return;

		const perpPosition = user.getPerpPosition(position?.marketIndex ?? 0);
		if (!perpPosition) return;

		const maintenanceHealth = user.getPerpPositionHealth({
			marginCategory: 'Maintenance',
			perpPosition,
		});
		const initialHealth = user.getPerpPositionHealth({
			marginCategory: 'Initial',
			perpPosition,
		});

		setMaintenancePositionWeightedValue(
			BigNum.from(maintenanceHealth.weightedValue, QUOTE_PRECISION_EXP)
		);
		setInitialPositionWeightedValue(
			BigNum.from(initialHealth.weightedValue, QUOTE_PRECISION_EXP)
		);
		setMaintenanceMarginRatio(
			parseFloat(
				(BigNum.from(maintenanceHealth.weight, 4).toNum() * 100).toFixed(2)
			)
		);
		setInitialMarginRatio(
			parseFloat(
				(BigNum.from(initialHealth.weight, 4).toNum() * 100).toFixed(2)
			)
		);
	};

	useEffect(() => {
		if (position && driftClient && driftClientIsReady) {
			getAndSetPositionHealth();
		}
	}, [driftClient, driftClientIsReady, position]);

	const marketPriceDecimals = getMarketPriceDecimals(
		uiMarket.uiSymbols.baseAssetSymbol
	);

	return (
		<SideDrawer.Container drawerName="position">
			<SideDrawer.Title innerWrapperClassName="border-0 pb-0">
				Position Details
			</SideDrawer.Title>
			<div
				className={cj(
					'flex items-center justify-between border-b border-stroke-divider',
					MODAL_PADDING.horizontal,
					MODAL_PADDING.headerFooterVertical
				)}
			>
				<MarketCellV2
					marketSymbol={marketConfig.symbol}
					direction={direction}
					marketType={MarketType.PERP}
					marketIndex={marketIndex}
					textClass="typo-label-sm"
					isIsolated={isIsolated}
				/>
				<div className="flex flex-col items-end gap-y-xs">
					<span className="typo-body-xs text-text-secondary">P&L / ROI%</span>
					<span
						className={cj(
							'typo-label-sm whitespace-nowrap',
							signDependentValue(netPnl.toNum(), {
								positive: 'text-text-positive-green-button',
								negative: 'text-text-negative-red-button',
								zero: 'text-text-default',
							})
						)}
					>
						{netDollarPnl} / {percentagePnl}
					</span>
				</div>
			</div>
			<SideDrawer.Body>
				<h3 className="typo-label-sm text-text-default">Position Info</h3>
				<div className="flex flex-col gap-y-sm">
					<DetailRow
						label="Entry Price"
						value={
							<NumberDisplay
								value={entryPrice}
								type="currency"
								decimals={marketPriceDecimals}
							/>
						}
					/>
					<DetailRow
						label={pnlReference === 'mark' ? 'Mark Price' : 'Oracle Price'}
						value={
							<NumberDisplay
								value={
									pnlReference === 'mark'
										? (markPrice ?? ZERO_PRICE_BIGNUM)
										: (oraclePrice ?? ZERO_PRICE_BIGNUM)
								}
								type="currency"
								decimals={marketPriceDecimals}
							/>
						}
					/>
					<DetailRow
						label="Cost Basis"
						value={
							<NumberDisplay
								value={BigNum.from(position?.costBasis, PRICE_PRECISION_EXP)}
								type="currency"
								decimals={marketPriceDecimals}
								isAccountValueToHide
							/>
						}
					/>
					<DetailRow
						label="Break Even Price"
						value={
							<NumberDisplay
								value={breakEvenPrice}
								type="currency"
								decimals={marketPriceDecimals}
							/>
						}
						tooltipDefinitionId="tradingBreakEvenPrice"
					/>
					<DetailRow
						label="Liquidation Price"
						value={
							<NumberDisplay
								value={liqPrice}
								type="currency"
								decimals={marketPriceDecimals}
							/>
						}
					/>
				</div>
				<Divider />
				<div className="flex flex-col gap-y-sm">
					<DetailRow
						label="Maintenance Margin Used"
						value={`${maintenancePositionWeightedValue.toNotional()} (${maintenanceMarginRatio}%)`}
					/>
					<DetailRow
						label="Initial Margin Used"
						value={`${initialPositionWeightedValue.toNotional()} (${initialMarginRatio}%)`}
					/>
				</div>
				<Divider />
				<div className="flex justify-between">
					<h3 className="typo-label-sm text-text-default">Est. Net P&L</h3>
					<div className="flex items-center gap-x-sm">
						<TextButton
							variant="secondary"
							size="sm"
							onClick={togglePnlReference}
							rightIcon={Swap}
						>
							{pnlReference === 'mark' ? 'Mark Price' : 'Oracle Price'}
						</TextButton>
						{estNetPnl}
					</div>
				</div>
				<div className="flex flex-col gap-y-sm">
					<DetailRow label="Notional Size" value={notional} />
					<DetailRow
						label="Position P&L"
						value={
							<span
								className={signDependentValue(pnlToUse.toNum(), {
									positive: 'text-text-positive-green-button',
									negative: 'text-text-negative-red-button',
									zero: 'text-text-default',
								})}
							>
								{positionDollarPnl}
							</span>
						}
					/>
					<DetailRow
						label="Fees and Funding"
						value={
							<span
								className={signDependentValue(fundinPnlNotional.toNum(), {
									positive: 'text-text-positive-green-button',
									negative: 'text-text-negative-red-button',
									zero: 'text-text-default',
								})}
							>
								{fundingPnlDisplay}
							</span>
						}
					/>
					{estHourlyFundingDisplay !== null && (
						<DetailRow
							label="Est. Next Hour Funding"
							value={
								<span
									className={signDependentValue(estHourlyFunding ?? 0, {
										positive: 'text-text-positive-green-button',
										negative: 'text-text-negative-red-button',
										zero: 'text-text-default',
									})}
								>
									{estHourlyFundingDisplay}
								</span>
							}
						/>
					)}
				</div>
			</SideDrawer.Body>
			<SideDrawer.Footer>
				<Button
					size="lg"
					variant="secondary"
					className="w-full"
					onClick={handleShowClosePositionModal}
					disabled={isClosingPosition}
				>
					Close Position
				</Button>
			</SideDrawer.Footer>
		</SideDrawer.Container>
	);
};
